import type { FourPillars, PillarPosition } from '../types/pillars';
import type { GuiinMatch } from '../types/compatibility';
import { stemIndex, branchIndex } from '../core/sexagenary';
import { CHEONUL_GUIIN, ILDEOK_GUIIN, WOLDEOK_GUIIN, CHEONDEOK_GUIIN, WOLHAP, getSamhapGroup } from '../sinsal/definitions';

/**
 * 두 사주 간 귀인 교차 매칭을 분석한다.
 *
 * - gungsal1: 천을귀인 — 일간 기준, 상대 지지에 해당 귀인이 있는지
 * - gungsal2: 월덕귀인 — 월지 삼합 그룹 기준, 상대 천간에 해당 귀인이 있는지
 * - gungsal3: 천덕귀인 — 월지 기준, 상대 천간/지지에 해당 귀인이 있는지
 * - gungsal4: 월합 — 월지 기준, 상대 천간에 해당 귀인이 있는지
 */
export function analyzeGuiinMatching(
  fp1: FourPillars,
  fp2: FourPillars,
  noHour: boolean,
): GuiinMatch[] {
  const matches: GuiinMatch[] = [];
  const positions: PillarPosition[] = noHour
    ? ['year', 'month', 'day']
    : ['year', 'month', 'day', 'hour'];

  // Check both directions: person1→person2 and person2→person1
  checkGuiin(fp1, fp2, 1, positions, matches);
  checkGuiin(fp2, fp1, 2, positions, matches);

  // 상호귀인 감지: 양방향에서 동일 타입이 성립한 경우 mutual=true
  // (천을귀인·일덕귀인만 일간/일지 기반 강귀인으로 취급)
  const MUTUAL_TYPES: GuiinMatch['type'][] = ['천을귀인', '일덕귀인'];
  for (const t of MUTUAL_TYPES) {
    const hasP1ToP2 = matches.some(m => m.type === t && m.sourcePerson === 1);
    const hasP2ToP1 = matches.some(m => m.type === t && m.sourcePerson === 2);
    if (hasP1ToP2 && hasP2ToP1) {
      for (const m of matches) {
        if (m.type === t) m.mutual = true;
      }
    }
  }

  return matches;
}

function checkGuiin(
  source: FourPillars,
  target: FourPillars,
  sourcePerson: 1 | 2,
  positions: PillarPosition[],
  matches: GuiinMatch[],
): void {
  const dayIdx = stemIndex(source.day.stem);

  // gungsal1: 천을귀인
  const guiinBranches = CHEONUL_GUIIN[dayIdx];
  if (guiinBranches) {
    for (const pos of positions) {
      const bIdx = branchIndex(target[pos].branch);
      if (guiinBranches.includes(bIdx)) {
        matches.push({
          type: '천을귀인',
          sourcePerson,
          matchedPillar: pos,
          description: `${sourcePerson === 1 ? 'P1' : 'P2'}의 일간(${source.day.stem}) 천을귀인이 ${sourcePerson === 1 ? 'P2' : 'P1'}의 ${pillarName(pos)}지에 존재`,
        });
      }
    }
  }

  // 일덕귀인 — 일간 기준, 상대 지지에 해당 지지가 있으면 성립
  const ildeokBranches = ILDEOK_GUIIN[dayIdx];
  if (ildeokBranches) {
    for (const pos of positions) {
      const bIdx = branchIndex(target[pos].branch);
      if (ildeokBranches.includes(bIdx)) {
        matches.push({
          type: '일덕귀인',
          sourcePerson,
          matchedPillar: pos,
          description: `${sourcePerson === 1 ? 'P1' : 'P2'}의 일간(${source.day.stem}) 일덕귀인이 ${sourcePerson === 1 ? 'P2' : 'P1'}의 ${pillarName(pos)}지에 존재`,
        });
      }
    }
  }

  // gungsal2: 월덕귀인 — 월지 삼합 그룹 → 상대 천간
  const monthBranchIdx = branchIndex(source.month.branch);
  const samhapGroup = getSamhapGroup(monthBranchIdx);
  const woldeokStemIdx = WOLDEOK_GUIIN[samhapGroup];
  if (woldeokStemIdx !== undefined) {
    for (const pos of positions) {
      const sIdx = stemIndex(target[pos].stem);
      if (sIdx === woldeokStemIdx) {
        matches.push({
          type: '월덕귀인',
          sourcePerson,
          matchedPillar: pos,
          description: `${sourcePerson === 1 ? 'P1' : 'P2'}의 월지(${source.month.branch}) 월덕귀인이 ${sourcePerson === 1 ? 'P2' : 'P1'}의 ${pillarName(pos)}간에 존재`,
        });
      }
    }
  }

  // gungsal3: 천덕귀인 — 월지 → 상대 천간 또는 지지
  const cheondeok = CHEONDEOK_GUIIN[monthBranchIdx];
  if (cheondeok) {
    for (const pos of positions) {
      if (cheondeok.type === 'stem') {
        const sIdx = stemIndex(target[pos].stem);
        if (sIdx === cheondeok.index) {
          matches.push({
            type: '천덕귀인',
            sourcePerson,
            matchedPillar: pos,
            description: `${sourcePerson === 1 ? 'P1' : 'P2'}의 월지(${source.month.branch}) 천덕귀인이 ${sourcePerson === 1 ? 'P2' : 'P1'}의 ${pillarName(pos)}간에 존재`,
          });
        }
      } else {
        const bIdx = branchIndex(target[pos].branch);
        if (bIdx === cheondeok.index) {
          matches.push({
            type: '천덕귀인',
            sourcePerson,
            matchedPillar: pos,
            description: `${sourcePerson === 1 ? 'P1' : 'P2'}의 월지(${source.month.branch}) 천덕귀인이 ${sourcePerson === 1 ? 'P2' : 'P1'}의 ${pillarName(pos)}지에 존재`,
          });
        }
      }
    }
  }

  // gungsal4: 월합 — 월지 → 상대 천간
  const wolhapStemIdx = WOLHAP[monthBranchIdx];
  if (wolhapStemIdx !== undefined) {
    for (const pos of positions) {
      const sIdx = stemIndex(target[pos].stem);
      if (sIdx === wolhapStemIdx) {
        matches.push({
          type: '월합',
          sourcePerson,
          matchedPillar: pos,
          description: `${sourcePerson === 1 ? 'P1' : 'P2'}의 월지(${source.month.branch}) 월합이 ${sourcePerson === 1 ? 'P2' : 'P1'}의 ${pillarName(pos)}간에 존재`,
        });
      }
    }
  }
}

function pillarName(pos: PillarPosition): string {
  switch (pos) {
    case 'year': return '년';
    case 'month': return '월';
    case 'day': return '일';
    case 'hour': return '시';
  }
}
